import React, { useState, useEffect, useRef } from "react";
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from './data/supabaseClient';
import { useTranslation } from 'react-i18next';
import { useToast } from './hooks/ToastContext';
import GoogleSignInButton from "./GoogleSignInButton";
import ParticleSystem from "./ParticleSystem";
import FloatingOrbs from "./FloatingOrbs";
import Spinner from './common/Spinner';
import { Button } from './Templates';
import './LoginForm.css';

// Import benefit images
import DriverImage from './Media/Driver.png';
import PassengerImage from './Media/Passenger.png';
import PackageImage from './Media/Package.png';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  
  // Form data
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  
  // Check for success message from password reset
  useEffect(() => {
    if (location.state?.message) {
      addToast({
        type: 'success',
        title: t('auth.password_reset_success'),
        description: location.state.message
      });
      
      // Pre-fill email if provided
      if (location.state.email) {
        setLoginEmail(location.state.email);
      }
      
      // Clear the state to prevent showing message again
      window.history.replaceState({}, document.title);
    }
  }, [location.state, addToast, t]);
  
  // Container ref for particle system
  const containerRef = useRef(null);
  const gridRef = useRef(null);
  
  // Mouse position tracking for grid only
  const [gridTransform, setGridTransform] = useState({ x: 0, y: 0 });

  // Form states
  const [showSignup, setShowSignup] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [signupError, setSignupError] = useState(null);

  // Grid mouse movement handler
  useEffect(() => {
    let animationFrameId = null;

    const handleMouseMove = (e) => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);

      animationFrameId = requestAnimationFrame(() => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          const xRatio = (e.clientX - rect.left - centerX) / centerX;
          const yRatio = (e.clientY - rect.top - centerY) / centerY;

          // Grid movement - subtle effect
          setGridTransform({
            x: xRatio * 5,
            y: yRatio * 5
          });
        }
      });
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => {
        container.removeEventListener('mousemove', handleMouseMove);
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
      };
    }
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsLoginLoading(true);
    setLoginError(null);
  
    try {
      if (!loginEmail || !loginPassword) {
        setLoginError(t('auth.email_password_required'));
        setIsLoginLoading(false);
        return;
      }
  
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email: loginEmail, 
        password: loginPassword 
      });
  
      if (error) throw error;
  
      const { data: profile } = await supabase
        .from('profiles')
        .select('username, completion_step')
        .eq('id', data.user.id)
        .single();
  
      addToast({
        type: 'success',
        title: t('auth.login_success'),
        description: profile?.username ? 
          t('auth.welcome_back', { name: profile.username }) : 
          t('auth.welcome')
      });
  
      if (!profile?.username) {
        navigate('/complete-profile');
      } else {
        navigate('/home');
      }
    } catch (error) {
      console.error("Login error:", error);
      
      if (error.message.includes('Invalid login')) {
        setLoginError(t('auth.invalid_credentials'));
      } else if (error.message.includes('rate limit')) {
        setLoginError(t('auth.too_many_attempts'));
      } else {
        setLoginError(t('auth.login_error'));
      }
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleSignUp = async (event) => {
    event.preventDefault();
    setIsSignupLoading(true);
    setSignupError(null);

    try {
      if (!signupEmail || !signupPassword || !signupConfirmPassword) {
        setSignupError(t('auth.fill_all_fields'));
        setIsSignupLoading(false);
        return;
      }
      
      if (signupPassword !== signupConfirmPassword) {
        setSignupError(t('auth.passwords_dont_match'));
        setIsSignupLoading(false);
        return;
      }

      if (signupPassword.length < 6) {
        setSignupError(t('auth.password_too_short'));
        setIsSignupLoading(false);
        return;
      }
      
      const { data, error } = await supabase.auth.signUp({
        email: signupEmail,
        password: signupPassword,
        options: {
          data: {
            email: signupEmail,
          }
        }
      });

      if (error) throw error;

      addToast({
        type: 'success',
        title: t('auth.signup_success_title'),
        description: t('auth.signup_successful')
      });
      
      setSignupEmail('');
      setSignupPassword('');
      setSignupConfirmPassword('');
      await supabase.auth.signOut();

      setShowSignup(false);
      setLoginEmail(signupEmail);

    } catch (error) {
      console.error("Error during signup:", error);
      
      if (error.message?.includes('already registered')) {
        setSignupError(t('auth.email_already_exists'));
      } else {
        setSignupError(error.message || t('auth.signup_error'));
      }
    } finally {
      setIsSignupLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!loginEmail) {
      setLoginError(t('auth.enter_email_for_reset'));
      return;
    }
    
    setIsLoginLoading(true);
    
    try {
      // Get the appropriate redirect URL based on environment
      const isDevelopment = window.location.hostname === 'localhost';
      const redirectUrl = isDevelopment 
        ? 'http://localhost:3000/reset-password'
        : 'https://brauksimkopa.lv/reset-password';
      
      console.log('Sending password reset email with redirect to:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(loginEmail, {
        redirectTo: redirectUrl
      });
      
      if (error) throw error;
      
      addToast({
        type: 'info',
        title: t('auth.password_reset_sent'),
        description: t('auth.check_email_for_reset')
      });
    } catch (error) {
      console.error("Password reset error:", error);
      setLoginError(t('auth.password_reset_error'));
    } finally {
      setIsLoginLoading(false);
    }
  };

  return (
    <div ref={containerRef} className="enhanced-login-page">
      {/* Landing Benefits Section */}
      <div className="landing-benefits-section">
        <div className="benefits-container">
          <motion.h2 
            className="benefits-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {t('landing.discover_benefits') || 'Discover the Benefits of ConRide'}
          </motion.h2>
          
          <motion.p 
            className="benefits-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t('landing.join_community') || 'Join thousands of users who save money and help the environment'}
          </motion.p>

          <div className="benefits-grid">
            <motion.div 
              className="benefit-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="benefit-image">
                <img src={DriverImage} alt="Driver Benefits" />
              </div>
              <h3>{t('benefits.driver_title') || 'As a Driver'}</h3>
              <ul>
                <li>{t('benefits.driver_point1') || 'Cover fuel costs'}</li>
                <li>{t('benefits.driver_point2') || 'Meet new people'}</li>
                <li>{t('benefits.driver_point3') || 'Help the environment'}</li>
              </ul>
            </motion.div>

            <motion.div 
              className="benefit-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="benefit-image">
                <img src={PassengerImage} alt="Passenger Benefits" />
              </div>
              <h3>{t('benefits.passenger_title') || 'As a Passenger'}</h3>
              <ul>
                <li>{t('benefits.passenger_point1') || 'Save up to 70%'}</li>
                <li>{t('benefits.passenger_point2') || 'Travel comfortably'}</li>
                <li>{t('benefits.passenger_point3') || 'Verified drivers'}</li>
              </ul>
            </motion.div>

            <motion.div 
              className="benefit-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="benefit-image">
                <img src={PackageImage} alt="Package Benefits" />
              </div>
              <h3>{t('benefits.package_title') || 'Package Delivery'}</h3>
              <ul>
                <li>{t('benefits.package_point1') || 'Fast delivery'}</li>
                <li>{t('benefits.package_point2') || 'Affordable prices'}</li>
                <li>{t('benefits.package_point3') || 'Track your package'}</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Animated background grid */}
      <div className="animated-bg-grid">
        <div 
          ref={gridRef}
          className="grid-pattern" 
          style={{
            transform: `translate(${gridTransform.x}px, ${gridTransform.y}px) scale(${1 + Math.abs(gridTransform.x + gridTransform.y) * 0.001})`
          }}
        />
      </div>

      {/* Particle System - Enhanced with more particles */}
      <ParticleSystem 
        particleCount={30} 
        mouseInfluence={0}
        containerRef={containerRef} 
      />

      {/* ConRide Logo - simplified without mouse interaction */}
      <div className="conride-logo-container">
        <p className="logo-tagline">
          {t('app.tagline') || 'Connect • Ride • Deliver'}
        </p>
      </div>



      {/* Login/Signup form - centered */}
      <div className="auth-form-container">
        {/* Physics-based floating orbs - 3 orbs with medium cursor attraction */}
        <FloatingOrbs 
          containerRef={containerRef}
          orbCount={3}
          minSize={100}
          maxSize={250}
          cursorAttraction="low"
        />
        
        <div className="auth-form-box">
          {!showSignup ? (
            <>
              <form onSubmit={handleLogin}>
                <div className="form-field">
                  <input
                    type="email"
                    placeholder={t('auth.email_placeholder')}
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="enhanced-input"
                  />
                </div>
                
                <div className="form-field">
                  <input
                    type="password"
                    placeholder={t('auth.password_placeholder')}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="enhanced-input"
                  />
                </div>
                
                {loginError && (
                  <div className="error-message">{loginError}</div>
                )}
                
                <Button
                  type="submit"
                  variant="primary"
                  className="auth-submit"
                  disabled={isLoginLoading}
                  loading={isLoginLoading}
                >
                  {t('buttons.login')}
                </Button>
              </form>
              
              <Button
                variant="tertiary"
                onClick={handleForgotPassword}
                className="auth-link"
                disabled={isLoginLoading}
              >
                {t('auth.forgot_password')}
              </Button>
              
              <div className="divider">
                <span>{t('auth.or_continue_with')}</span>
              </div>
              
              <GoogleSignInButton />
              
              <p className="switch-form-text">
                {t("auth.dont_have_account")}
                <button
                  onClick={() => setShowSignup(true)}
                  className="link-button inline"
                >
                  {t('buttons.signup')}
                </button>
              </p>
            </>
          ) : (
            <>
              <h2>{t('auth.signup_form')}</h2>
              
              <form onSubmit={handleSignUp}>
                <div className="form-field">
                  <input
                    type="email"
                    placeholder={t('auth.email_placeholder')}
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className="enhanced-input"
                  />
                </div>
                
                <div className="form-field">
                  <input
                    type="password"
                    placeholder={t('auth.password_placeholder')}
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    className="enhanced-input"
                  />
                </div>
                
                <div className="form-field">
                  <input
                    type="password"
                    placeholder={t('auth.confirm_password')}
                    value={signupConfirmPassword}
                    onChange={(e) => setSignupConfirmPassword(e.target.value)}
                    className="enhanced-input"
                  />
                </div>
                
                {signupError && (
                  <div className="error-message">{signupError}</div>
                )}
                
                <Button
                  type="submit"
                  variant="primary"
                  className="auth-submit"
                  disabled={isSignupLoading}
                  loading={isSignupLoading}
                >
                  {t('buttons.signup')}
                </Button>
              </form>
              
              <p className="switch-form-text">
                {t('auth.already_have_account')}
                <button
                  onClick={() => setShowSignup(false)}
                  className="link-button inline"
                >
                  {t('auth.back_to_login')}
                </button>
              </p>
            </>
          )}
        </div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div
        className="scroll-indicator"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="scroll-wheel">
          <div className="scroll-dot"></div>
        </div>
      </motion.div>
    </div>
  );
}

export default Login;
